import { Formik, FormikConfig, FormikHelpers } from 'formik';
import { filter, map, sortBy } from 'lodash';
import { memo, useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';

import { useDispatch } from 'react-redux';
import { PageTitle } from '@modules/admin/Tools/LenderUtility/shared/PageTitle';
import { SERVICES } from '@shared/constants/apiConfig';
import { ILendingLoader } from '@shared/components/IlendingLoader';

import { Wrapper } from '@styles/CommonStyles';
import { stateMenuItemRefresh } from '@store/reducers/layoutSlice';
import { IQueue, IRole } from '../QueueConfiguration/types';
import {
  IChildMenuItem,
  IChildMenuItemResponse,
  IMenuItem,
  IMenuItemPayload,
  IMenuItemResponse,
  IMenuValues,
  TChildMenuItemPayload,
  TNullableAnyItem,
} from './types';
import { MenuEditorForm } from './components/MenuEditorForm';
import { getExpandedIds } from './helpers';
import { requestUpdateMenu } from './requests';

const MenuEditorContainer = () => {
  const dispatch = useDispatch();

  const [expanded, setExpanded] = useState<string[]>([]);
  const [selectedMenuItem, setSelectedMenuItem] = useState<TNullableAnyItem>(null);
  const [loading, setLoading] = useState(false);

  // #region API INTEGRATION
  const { data: menuItemsResponse, isLoading: isMenuItemsLoading, mutate } = useSWR<IMenuItemResponse[]>(`${SERVICES.CONFIGURATION}/menu/items`, {
    onSuccess: (data) => setExpanded(getExpandedIds(data)),
  });

  const { data: rolesResponse, isLoading: isRolesResponseLoading } = useSWR<IRole[]>(`${SERVICES.USER}/roles`);

  const { data: queueResponse, isLoading: isQueueResponseLoading } = useSWR<IQueue[]>(`${SERVICES.CONFIGURATION}/queue-configurations/groups-and-subgroups`);
  // #endregion

  const refreshMenuEditor = useCallback(() => {
    mutate();
    setSelectedMenuItem(null);
  }, []);

  const onSaveMenu = useCallback<FormikConfig<IMenuValues>['onSubmit']>(
    async({ menuItems: menuItemsValues },formikHelpers: FormikHelpers<IMenuValues>): Promise<void> => {
      const updatedMenuItems: IMenuItemPayload[] = map<IMenuItem, IMenuItemPayload>(
        menuItemsValues,
        (
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          { id, parentId, order, childMenuItems, ...menuItem },
          parentIndex,
        ) => ({
          ...menuItem,
          order: parentIndex,
          childMenuItems: map<IChildMenuItem, TChildMenuItemPayload>(
            childMenuItems,
            (
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              { id, parentId, order, ...childMenuItem },
              childIndex,
            ) => ({ ...childMenuItem, order: childIndex }),
          ),
        }),
      );
      setLoading(true);
      await requestUpdateMenu(updatedMenuItems);
      refreshMenuEditor();
      const nextState: IMenuValues = { menuItems: menuItemsValues };
      formikHelpers.resetForm({ values: nextState });
      dispatch(stateMenuItemRefresh(true));
      setLoading(false);
    },
    [],
  );

  const menuItems = useMemo<IMenuItem[]>(
    () =>
      map<IMenuItemResponse, IMenuItem>(
        sortBy<IMenuItemResponse>(filter<IMenuItemResponse>(menuItemsResponse, ['isDeleted', false]), 'order'),
        (menuItem) => ({
          ...menuItem,
          parentId: null,
          childMenuItems: map<IChildMenuItemResponse, IChildMenuItem>(
            sortBy<IChildMenuItemResponse>(
              filter<IChildMenuItemResponse>(menuItem.childMenuItems, ['isDeleted', false]),
              'order',
            ),
            (childMenuItem) => ({ ...childMenuItem, parentId: menuItem.id }),
          ),
        }),
      ),
    [menuItemsResponse],
  );

  const roles = useMemo<IRole[]>(() => sortBy<IRole>(rolesResponse, 'name'), [rolesResponse]);

  const queueOptions = useMemo<IQueue[]>(() => sortBy<IQueue>(queueResponse, 'group'), [queueResponse]);

  const isLoading = useMemo(
    () => isMenuItemsLoading || isRolesResponseLoading || isQueueResponseLoading,
    [isMenuItemsLoading, isRolesResponseLoading, isQueueResponseLoading],
  );

  return (
    <Wrapper>
      <PageTitle mt={'16px'}>Menu Editor</PageTitle>

      {isLoading ? (
        <ILendingLoader />
      ) : (
        <Formik enableReinitialize initialValues={{ menuItems }} onSubmit={onSaveMenu}>
          <MenuEditorForm
            expanded={expanded}
            selectedMenuItem={selectedMenuItem}
            roleOptions={roles}
            queueOptions={queueOptions}
            loading={loading}
            setExpanded={setExpanded}
            setSelectedMenuItem={setSelectedMenuItem}
          />
        </Formik>
      )}
    </Wrapper>
  );
};

export const MenuEditor = memo(MenuEditorContainer);
